import { getAccountApi } from '../api/clients';
import { getAppConfig } from '../config/appConfigRef';
import { getLoginRedirectUri } from '../config/getElvaLoginUrl';
import { readOAuthRedirectUri } from './pkce';
import type { ApiEnvelope, SessionPayload } from './types';

export interface OAuthClientRegistration {
  client_id: string;
  name: string;
  permission: string | null;
}

/** Public — same check the account app runs before showing the login form. */
export async function validateOAuthClientRegistration(
  redirectUri: string,
): Promise<OAuthClientRegistration> {
  const config = getAppConfig();
  const response = await getAccountApi().get<ApiEnvelope<{ client: OAuthClientRegistration }>>(
    `/auth/clients/${encodeURIComponent(config.clientId)}`,
    { params: { redirect_uri: redirectUri } },
  );

  return response.data.data.client;
}

/**
 * Public. What authenticates the call is the `code_verifier`, which never left the app.
 * The code lives 60 seconds, is single use, and is burned before any check runs — so a
 * failure here always means "restart the flow", never "retry".
 */
export async function redeemHandoffCode(
  code: string,
  codeVerifier: string,
): Promise<SessionPayload> {
  const config = getAppConfig();
  const redirectUri = readOAuthRedirectUri() ?? getLoginRedirectUri();

  const response = await getAccountApi().post<ApiEnvelope<SessionPayload>>(
    '/auth/handoff/redeem',
    {
      code,
      code_verifier: codeVerifier,
      client_id: config.clientId,
      redirect_uri: redirectUri,
    },
  );

  return response.data.data;
}

/** Public. Returns a new access token **and a new refresh token** — persist both at once. */
export async function refreshSessionRequest(refreshToken: string): Promise<SessionPayload> {
  const response = await getAccountApi().post<ApiEnvelope<SessionPayload>>('/auth/refresh', {
    refresh_token: refreshToken,
  });

  return response.data.data;
}

/** Re-mints the access token at another location. No `refreshToken` comes back. */
export async function switchLocationRequest(locationId: string): Promise<SessionPayload> {
  const response = await getAccountApi().post<ApiEnvelope<SessionPayload>>(
    '/auth/switch-location',
    { location_id: locationId },
  );

  return response.data.data;
}

export async function logoutSession(refreshToken: string): Promise<void> {
  await getAccountApi().post<ApiEnvelope<{ loggedOut: boolean }>>('/auth/logout', {
    refresh_token: refreshToken,
  });
}

/** Ends every session this user holds on every Elva app. */
export async function logoutAllSessions(): Promise<void> {
  await getAccountApi().post<ApiEnvelope<{ loggedOut: boolean; scope: string }>>(
    '/auth/logout-all',
  );
}
